import { Link } from '@tanstack/react-router';
import { bookingMoney, type BookingDetail } from '@/lib/bookings';
import { type Apartment } from '@/lib/apartments';
import { formatDate, formatDateUTC, formatMoney } from '@/lib/format';
import {
  BookingSourceBadge,
  OperationalStatusBadge,
} from '@/components/StatusBadges';

// ─── Card / Row ──────────────────────────────────────────────────────────────

export function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-muted">{title}</h2>
      {children}
    </section>
  );
}

export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="w-36 shrink-0 text-muted">{label}</span>
      <span className="flex-1">{children}</span>
    </div>
  );
}

// ─── Nights count ─────────────────────────────────────────────────────────────

export function nightsCount(checkin: string, checkout: string): number {
  const a = new Date(`${checkin}T12:00:00Z`);
  const b = new Date(`${checkout}T12:00:00Z`);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

// ─── Guest card ───────────────────────────────────────────────────────────────

export function GuestCard({ booking }: { booking: BookingDetail }) {
  return (
    <Card title="Guest">
      <div className="space-y-2">
        {booking.guest_id ? (
          // guest_name is the booking-time snapshot; the link resolves to the
          // canonical guest record (ADR-031) which may carry a different name.
          <Link
            to="/guests/$id"
            params={{ id: booking.guest_id }}
            className="text-lg font-semibold text-primary hover:underline"
            aria-label={`View guest profile for ${booking.guest_name}`}
          >
            {booking.guest_name}
          </Link>
        ) : (
          <p className="text-lg font-semibold">{booking.guest_name}</p>
        )}
        {!booking.guest_id && (
          <p className="text-xs text-muted">Not linked to a guest record.</p>
        )}
        <div className="space-y-1.5">
          {booking.guest_email && (
            <Row label="Email">
              <a
                href={`mailto:${booking.guest_email}`}
                className="text-primary hover:underline"
              >
                {booking.guest_email}
              </a>
            </Row>
          )}
          {booking.guest_phone && (
            <Row label="Phone">
              <a
                href={`tel:${booking.guest_phone}`}
                className="text-primary hover:underline"
              >
                {booking.guest_phone}
              </a>
            </Row>
          )}
          <Row label="Adults">{booking.adults}</Row>
          {booking.children > 0 && <Row label="Children">{booking.children}</Row>}
        </div>
      </div>
    </Card>
  );
}

// ─── Booking card ─────────────────────────────────────────────────────────────

export function BookingCard({
  booking,
  aptMap,
}: {
  booking: BookingDetail;
  aptMap: Map<string, Apartment>;
}) {
  const apt = aptMap.get(booking.apartment_id);
  const nights = nightsCount(booking.checkin_date, booking.checkout_date);
  const { commission, taxOnTop, payout } = bookingMoney(booking);

  return (
    <Card title="Booking">
      <div className="space-y-1.5">
        <Row label="Apartment">
          {apt ? (
            <span>
              <span className="font-mono text-xs font-semibold">{apt.code}</span>
              <span className="ml-1 text-muted">· {apt.title}</span>
            </span>
          ) : (
            <span className="font-mono text-xs text-muted">{booking.apartment_id}</span>
          )}
        </Row>
        <Row label="Stay">
          <OperationalStatusBadge status={booking.operational_status} />
        </Row>
        <Row label="Check-in">{formatDate(booking.checkin_date)}</Row>
        <Row label="Check-out">{formatDate(booking.checkout_date)}</Row>
        <Row label="Nights">{nights}</Row>
        <Row label="Guests">
          <span className="tabular-nums">
            {booking.adults}A{booking.children > 0 ? ` · ${booking.children}C` : ''}
            {booking.infants > 0 ? ` · ${booking.infants}I` : ''}
          </span>
        </Row>
        <Row label="Source">
          <BookingSourceBadge source={booking.source} />
        </Row>
        <Row label="Total">
          <span className="font-semibold tabular-nums">
            {formatMoney(booking.total_amount_minor, booking.currency)}
          </span>
        </Row>
        {taxOnTop !== null && (
          <Row label="Tourism tax">
            <span className="tabular-nums text-muted">
              {formatMoney(taxOnTop, booking.currency)}
              <span className="ml-1 text-xs">· collected on arrival</span>
            </span>
          </Row>
        )}
        {commission !== null && payout !== null && (
          <>
            <Row label="OTA commission">
              <span className="tabular-nums">
                {formatMoney(commission, booking.currency)}
              </span>
            </Row>
            <Row label="Payout">
              <span className="tabular-nums">
                {formatMoney(payout, booking.currency)}
              </span>
            </Row>
          </>
        )}
        {booking.ota_reservation_code && (
          <Row label="OTA Ref">
            <code className="font-mono text-xs">{booking.ota_reservation_code}</code>
          </Row>
        )}
        <Row label="Booked">{formatDateUTC(booking.created_at)}</Row>
      </div>
    </Card>
  );
}

// ─── Meta card ────────────────────────────────────────────────────────────────

export function MetaCard({ meta }: { meta: Record<string, unknown> }) {
  if (Object.keys(meta).length === 0) return null;
  return (
    <Card title="Raw Meta">
      <pre className="overflow-x-auto rounded-md bg-[var(--surface-sunken)] p-3 text-xs text-foreground">
        {JSON.stringify(meta, null, 2)}
      </pre>
    </Card>
  );
}
